import { memo } from 'react';
import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Loader2 } from 'lucide-react';
import WorkspaceCardFrame from './WorkspaceCardFrame.jsx';
import WorkspaceInitialsAvatar from './WorkspaceInitialsAvatar.jsx';
import StudioChips from './StudioChips.jsx';
import { studioLabelKey } from '../workspaceSelectorData.js';

/**
 * A non-active workspace in the "OTHER WORKSPACES" list: quieter and shorter
 * than the hero card. Selecting it switches to that workspace; the studio
 * chips deep-link into a studio instead.
 *
 * Memoized list item — the parent passes stable handlers keyed by workspace id.
 */
function WorkspaceCompactCard({ workspace, meta, disambiguator, switching = false, disabled = false, onSelect, onOpenStudio }) {
  const { t } = useTranslation('core-shell');

  return (
    <WorkspaceCardFrame
      label={t('workspaceSelector.openWorkspace', {
        name: disambiguator ? `${workspace.name} (${disambiguator})` : workspace.name,
      })}
      busy={switching}
      disabled={disabled}
      // Card body → the workspace's default studio (CT-4940), same as the hero.
      onSelect={() => onSelect(workspace.id, meta.defaultStudioId)}
      className="p-4"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        {/* Identity */}
        <div className="flex min-w-0 items-start gap-3 lg:w-72 lg:flex-shrink-0">
          <WorkspaceInitialsAvatar
            src={workspace.avatarSrc}
            initials={workspace.initials}
            name={workspace.name}
            accentClassName={meta.avatarClassName}
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-content">{workspace.name}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Badge variant={meta.industryVariant} size="small">
                {meta.industry}
              </Badge>
              <Badge variant={meta.roleVariant} size="small">
                {meta.role}
              </Badge>
            </div>
          </div>
        </div>

        {/* Studios */}
        <div className="pointer-events-auto min-w-0 flex-1">
          <StudioChips
            studios={meta.studios}
            disabled={switching || disabled}
            onOpenStudio={(studioId) => onOpenStudio(workspace.id, studioId)}
          />
        </div>

        {/* Last visited */}
        <div className="flex items-center gap-2 lg:w-44 lg:flex-shrink-0 lg:justify-end">
          {switching ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-content-muted">
              <Loader2 size={14} className="animate-spin" />
              {t('workspaceSelector.switching')}
            </span>
          ) : (
            <span className="text-right text-xs text-content-faint">
              {t('workspaceSelector.lastIn', {
                studio: t(studioLabelKey(meta.lastVisitedStudioId)),
                time: meta.lastVisitedLabel,
              })}
            </span>
          )}
        </div>
      </div>
    </WorkspaceCardFrame>
  );
}

export default memo(WorkspaceCompactCard);
